import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { enqueueTrackAction } from './useOfflineQueue';
import { showToast } from '../lib/toast';
import { DA_BROWSE_SELECT } from '../lib/utils';
import { readWithTimeout } from '../lib/supabaseRead';
import { readTrackedCache, writeTrackedCache, removeLegacyAccountCaches } from '../lib/userScopedCache';

const SAVE_FAIL_TOAST = "Couldn't save — check connection and retry.";

export function useTrackedDAs(session, { maxTracked = Infinity } = {}) {
  // Never hydrate authenticated state from an unscoped key. Start empty; the
  // identity effect below hydrates from this user's scoped cache (or clears to
  // nothing when signed out) so account B can never see account A's data.
  const [tracked, setTracked] = useState([]);
  const userId = session?.user?.id || null;

  // Async mutations may settle after an A→B (or A→B→A) identity transition.
  // A generation token plus mounted flag prevents an old continuation from
  // changing the new identity's state, cache, toast, or analytics surface.
  const identityRef = useRef({ userId, generation: 0, mounted: true });
  if (identityRef.current.userId !== userId) {
    identityRef.current = {
      userId,
      generation: identityRef.current.generation + 1,
      mounted: true,
    };
  }
  useEffect(() => () => { identityRef.current.mounted = false; }, []);
  const captureIdentity = () => ({
    userId: identityRef.current.userId,
    generation: identityRef.current.generation,
  });
  const isCurrentIdentity = token => Boolean(
    identityRef.current.mounted
    && token.userId === identityRef.current.userId
    && token.generation === identityRef.current.generation
  );

  // Skip the persist write on the render immediately after an identity change,
  // otherwise the previous user's `tracked` value would be written under the new
  // user's scoped key before the re-hydrate commit lands.
  const skipNextPersist = useRef(false);

  // Load from Supabase (authoritative source) when the signed-in user changes.
  // Keyed to the user id (not the session object) so token refreshes don't refetch,
  // guarded against stale writes, and every read has a timeout so a wedged request
  // can never leave the tracked list stale-until-restart.
  useEffect(() => {
    // Delete the legacy unscoped blob so no account can ever hydrate from it.
    removeLegacyAccountCaches();

    if (!userId) {
      // Signed-out state hydrates nothing.
      skipNextPersist.current = true;
      setTracked([]);
      return;
    }

    // Immediate hydrate from THIS user's scoped cache; also purges the previous
    // user's in-memory list before the first render of authenticated data.
    skipNextPersist.current = true;
    setTracked(readTrackedCache(userId));

    let cancelled = false;
    (async () => {
      const { data: tdata, error } = await readWithTimeout(
        signal => supabase
          .from('tracked_das')
          .select('id, da_id, created_at, pipeline_stage, pipeline_updated_at, quoted_amount, contact_name, contact_method, won_amount, lost_reason, next_follow_up_at, follow_up_reason, last_contacted_at, last_seen_status, last_seen_status_at')
          .eq('user_id', userId)
          .abortSignal(signal),
        { label: 'tracked_das_load' }
      );
      if (cancelled) return;
      if (error) { console.warn('[useTrackedDAs] load failed:', error.message); return; }
      if (!tdata || tdata.length === 0) { setTracked([]); return; }
      const daIds   = tdata.map(r => r.da_id);
      const tdataMap = Object.fromEntries(tdata.map(r => [r.da_id, r]));
      const [{ data: das }, { data: notes }] = await Promise.all([
        readWithTimeout(
          signal => supabase
            .from('da_browse_view')
            .select(DA_BROWSE_SELECT)
            .in('id', daIds)
            .abortSignal(signal),
          { label: 'tracked_das_rows_load' }
        ),
        readWithTimeout(
          signal => supabase
            .from('da_notes')
            .select('da_id, note_text, updated_at')
            .eq('user_id', userId)
            .in('da_id', daIds)
            .abortSignal(signal),
          { label: 'tracked_das_notes_load' }
        ),
      ]);
      if (cancelled) return;
      if (!das) return;
      const notesMap = Object.fromEntries((notes || []).map(r => [r.da_id, r]));
      setTracked(das.map(d => {
        const t = tdataMap[d.id] || {};
        const n = notesMap[d.id] || {};
        return {
          ...d,
          notes:               n.note_text || '',
          note_text:           n.note_text || '',
          note_updated_at:     n.updated_at || null,
          added:               (t.created_at || '').split('T')[0],
          tracked_id:          t.id          || null,
          pipeline_stage:      t.pipeline_stage      || 'watching',
          pipeline_updated_at: t.pipeline_updated_at || null,
          quoted_amount:       t.quoted_amount       || null,
          contact_name:        t.contact_name        || null,
          contact_method:      t.contact_method      || null,
          won_amount:          t.won_amount          || null,
          lost_reason:         t.lost_reason         || null,
          next_follow_up_at:   t.next_follow_up_at   || null,
          follow_up_reason:    t.follow_up_reason    || null,
          last_contacted_at:   t.last_contacted_at   || null,
          last_seen_status:    t.last_seen_status    || null,
          last_seen_status_at: t.last_seen_status_at || null,
        };
      }));
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Persist to this user's scoped cache as a fast cache. Never write an unscoped
  // key, and skip the transitional render right after an identity change so the
  // previous user's list is never written under the new user's key.
  useEffect(() => {
    if (skipNextPersist.current) { skipNextPersist.current = false; return; }
    if (!userId) return; // signed-out writes nothing
    writeTrackedCache(userId, tracked);
  }, [tracked, userId]);

  const togTrack = async (a) => {
    const operationIdentity = captureIdentity();
    const operationUserId = operationIdentity.userId;
    const isTracked = tracked.some(t => t.id === a.id);
    const limit = Number(maxTracked);

    if (!isTracked && Number.isFinite(limit) && tracked.length >= limit) {
      const daLabel = limit === 1 ? 'DA' : 'DAs';
      showToast(`Free Standard can track up to ${limit} ${daLabel}. Upgrade to Pro for more.`);
      return;
    }

    // Capture the existing row so a failed untrack can be rolled back exactly.
    const existing = tracked.find(t => t.id === a.id);

    // Optimistic local update regardless of connectivity
    if (isTracked) {
      setTracked(p => p.filter(t => t.id !== a.id));
    } else {
      setTracked(p => [...p, { ...a, notes: '', note_text: '', note_updated_at: null, added: new Date().toISOString().split('T')[0], tracked_id: null, pipeline_stage: 'watching', pipeline_updated_at: null, quoted_amount: null, contact_name: null, contact_method: null, won_amount: null, lost_reason: null, next_follow_up_at: null, follow_up_reason: null, last_contacted_at: null, last_seen_status: a.status || null, last_seen_status_at: new Date().toISOString() }]);
    }

    if (!operationUserId) return;

    if (!navigator.onLine) {
      await enqueueTrackAction({ action: isTracked ? 'untrack' : 'track', daId: a.id, userId: operationUserId });
      if (!isCurrentIdentity(operationIdentity)) return;
      showToast("You're offline. This will sync when you reconnect.");
      return;
    }

    if (isTracked) {
      // supabase-js returns { error } and does not throw — read it and roll back.
      const { error } = await supabase.from('tracked_das').delete().eq('user_id', operationUserId).eq('da_id', a.id);
      if (!isCurrentIdentity(operationIdentity)) return;
      if (error) {
        console.warn('[useTrackedDAs] untrack failed:', error.message);
        if (existing) setTracked(p => (p.some(t => t.id === a.id) ? p : [...p, existing]));
        showToast(SAVE_FAIL_TOAST);
      }
    } else {
      const { data: inserted, error } = await supabase
        .from('tracked_das')
        .insert({ user_id: operationUserId, da_id: a.id, last_seen_status: a.status || null, last_seen_status_at: new Date().toISOString() })
        .select('id')
        .single();
      if (!isCurrentIdentity(operationIdentity)) return;
      if (error) {
        console.warn('[useTrackedDAs] track failed:', error.message);
        setTracked(p => p.filter(t => t.id !== a.id));
        showToast(SAVE_FAIL_TOAST);
      } else if (inserted?.id) {
        setTracked(p => p.map(t => t.id === a.id ? { ...t, tracked_id: inserted.id } : t));
      }
    }
  };

  const isTr = id => tracked.some(t => t.id === id);

  return { tracked, setTracked, togTrack, isTr };
}
